import { CommonModule, Location } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, effect, EventEmitter, Inject, inject, Input, Output } from '@angular/core';
import { Router } from '@angular/router';

import { IonButtons, IonImg, IonTitle, IonToolbar } from '@ionic/angular/standalone';

import { register } from 'swiper/element/bundle';

import { ClientUserService, ProUserService } from '../../services';
import { EnvironmentInterface, GlobalsInterface, ProPortfolioInterface } from '../../types';
import { AvatarImageComponent } from '../avatar-image/avatar-image.component';
import {
    BackButtonComponent,
    CloseButtonComponent,
    FavoriteButtonComponent,
    IconButtonComponent,
    NotificationBellButtonComponent
} from '../buttons';
import { ButtonComponent } from '../buttons/button/button.component';
import { GalleryLightboxComponent } from '../gallery-lightbox/gallery-lightbox.component';

register();

export type HeaderLeftActionOptionsType = 'back-button' | 'back-button-history' | 'close' | 'back-custom' | 'none';
export type HeaderRightActionOptionsType =
    | 'notification'
    | 'close'
    | 'skip'
    | 'favorite'
    | 'reset'
    | 'settings'
    | 'none'
    | 'custom';

@Component({
    selector: 'lib-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css'],
    standalone: true,
    imports: [
        IonToolbar,
        CommonModule,
        IconButtonComponent,
        NotificationBellButtonComponent,
        CloseButtonComponent,
        ButtonComponent,
        IonButtons,
        IonTitle,
        IonImg,
        NotificationBellButtonComponent,
        FavoriteButtonComponent,
        BackButtonComponent,
        GalleryLightboxComponent,
        AvatarImageComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class HeaderComponent {
    /* eslint-disable */
    clientUserService = inject(ClientUserService);
    proUserService = inject(ProUserService);

    router = inject(Router);
    location = inject(Location);

    globals!: GlobalsInterface;

    @Input() type: 'logo' | 'text' | 'image-gallery' | 'user' = 'text';

    @Input() logoUrl: string = '../../../assets/logo/logo.svg';
    @Input() logoAltText: string = '';

    @Input() button: string = '';
    @Input() defaultLink: string = '';

    @Input() title: string = '';
    @Input() centerTitle: boolean = false;

    @Input() customBackButton: boolean = false;
    @Input() customBackButtonRedirect: string = '';

    @Input() leftActionButton: HeaderLeftActionOptionsType = 'none';
    @Input() rightActionButton: HeaderRightActionOptionsType = 'none';
    @Input() leftPadding: boolean = false;
    @Input() rightPadding: boolean = true;

    @Input() backButtonDefaultHref: string = '';
    @Input() backButtonWithBorder: boolean = false;

    @Input() leaveTitle: string = 'Cancel booking?';
    @Input() leaveMessage: string =
        'Are you sure you want to cancel booking with this Pro? All entered data will be lost';
    @Input() leaveConfirmText: string = 'Cancel booking';
    @Input() leaveCancelText: string = 'Back to booking';

    @Input() backButtonChevronPath: string = '../../../assets/svg-icons/Outline/Arrows/Alt Arrow Left.svg';
    @Input() backButtonArrowPath: string = '../../../assets/svg-icons/Outline/Arrows/Arrow Left.svg';

    @Input() proId: number | string | undefined = undefined;

    @Input() images: ProPortfolioInterface[] = [];

    @Input() backgroundColor: string | undefined = undefined;
    @Input() contentColor: string | undefined = undefined;
    @Input() rippleColor: string | undefined = undefined;

    @Input() useBackUrlFromNavExtras: boolean = true;

    @Output() closeButtonClick = new EventEmitter();
    @Output() skipButtonClick = new EventEmitter();
    @Output() leaveButtonClick = new EventEmitter();
    @Output() resetButtonClick = new EventEmitter();
    @Output() settingsButtonClick = new EventEmitter();
    @Output() backButtonClick = new EventEmitter();

    user: any | null = null;

    today = new Date();

    backUrlFromNavExtras: string | null = null;

    env!: EnvironmentInterface;

    constructor(
        @Inject('globals') globals: GlobalsInterface,
        @Inject('environment') environment: EnvironmentInterface
    ) {
        this.globals = globals;
        this.env = environment;

        this.backUrlFromNavExtras = this.router.getCurrentNavigation()?.extras.state?.['data']['backUrl'] || null;

        effect(() => {
            if (this.type === 'user') {
                if (this.globals.appType === 'client') {
                    this.user = this.clientUserService.user() || null;
                } else if (this.globals.appType === 'pro') {
                    this.user = this.proUserService.user() || null;
                }
            }
        });
    }

    backClick() {
        this.backButtonClick.emit();

        if (this.leftActionButton === 'back-button') {
            const url = this.useBackUrlFromNavExtras ? this.backUrlFromNavExtras : this.backButtonDefaultHref;
            this.router.navigateByUrl(url || this.backButtonDefaultHref, { replaceUrl: true });
        } else {
            this.navigateTo(this.defaultLink, true, true);
        }
    }

    onCloseButtonClick() {
        this.closeButtonClick.emit();
    }

    onSkipButtonClick() {
        this.skipButtonClick.emit();
    }

    onResetButtonClick() {
        this.resetButtonClick.emit();
    }

    onSettingsButtonClick() {
        this.settingsButtonClick.emit();
    }

    navigateTo(page: string, useHistory?: boolean, replaceUrl?: boolean) {
        if (useHistory) {
            this.location.back();
            return;
        }

        this.router.navigateByUrl(`/${page}`, { replaceUrl });
    }
}
